import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calculator, AlertTriangle, ArrowRight } from "lucide-react";

/** Corrige TRM / % gastos de un contenedor ya importado y recalcula el costo de sus lotes. */
export default function RecostearContenedorDialog({ contenedor, open, onClose, onDone }) {
  const [trm, setTrm] = useState("");
  const [pct, setPct] = useState("");
  const [landed, setLanded] = useState("");
  const [metodo, setMetodo] = useState("valor");
  const [motivo, setMotivo] = useState("");
  const [conPrecios, setConPrecios] = useState(false);
  const [pctMin, setPctMin] = useState("");
  const [pctSug, setPctSug] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const pctActual = contenedor ? (Number(contenedor.pct_gastos_default) > 0 && Number(contenedor.pct_gastos_default) <= 1 ? Number(contenedor.pct_gastos_default) * 100 : Number(contenedor.pct_gastos_default || 0)) : 0;
  const landedActual = Number(contenedor?.gastos_landed_total_cop || 0);
  const metodoActual = contenedor?.metodo_prorrateo || "valor";

  useEffect(() => {
    if (!open || !contenedor) return;
    setTrm(String(contenedor.trm_aplicada ?? ""));
    setPct(String(pctActual));
    setLanded(String(landedActual));
    setMetodo(metodoActual);
    setPctMin(String(contenedor.pct_margen_minimo_default ?? ""));
    setPctSug(String(contenedor.pct_margen_sugerido_default ?? ""));
    setConPrecios(false); setMotivo(""); setPreview(null);
    // eslint-disable-next-line
  }, [open, contenedor]);

  const body = () => ({
    trm_aplicada: Number(trm), pct_gastos: Number(pct), motivo: motivo.trim() || null,
    gastos_landed_total_cop: Number(landed) || 0, metodo_prorrateo: metodo,
    actualizar_precios: conPrecios,
    pct_margen_minimo: conPrecios ? Number(pctMin) : null,
    pct_margen_sugerido: conPrecios ? Number(pctSug) : null,
  });
  const landedCambia = Math.abs((Number(landed) || 0) - landedActual) >= 0.5 || ((Number(landed) || 0) > 0 && metodo !== metodoActual);
  const sinCambio = contenedor && Number(trm) === Number(contenedor.trm_aplicada) && Number(pct) === pctActual && !landedCambia && !conPrecios;

  const calcular = async () => {
    if (!Number(trm) || Number(trm) <= 0) return toast.error("TRM inválida");
    if (conPrecios && (pctMin === "" || pctSug === "")) return toast.error("Indica los % de margen mínimo y sugerido");
    if (conPrecios && Number(pctSug) < Number(pctMin)) return toast.error("El margen sugerido no puede ser menor al mínimo");
    setLoading(true);
    try {
      const { data } = await api.post(`/productos/_/contenedores/${contenedor.id}/recostear/preview`, body());
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular"); }
    finally { setLoading(false); }
  };

  const aplicar = async () => {
    if (motivo.trim().length < 5) return toast.error("Escribe el motivo (mínimo 5 caracteres)");
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/_/contenedores/${contenedor.id}/recostear`, body());
      toast.success(`Costeo corregido · ${data.lotes} lotes · ${data.fichas_a_actualizar} fichas · ${data.despachos_a_recalcular} despachos recalculados`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar"); }
    finally { setSaving(false); }
  };

  if (!contenedor) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recostear-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Calculator className="w-5 h-5 text-primary" /> Corregir costeo · {contenedor.referencia}</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Cambia la TRM, el % de gastos o los gastos landed (COP fijos) y el sistema recalcula el costo de todos los lotes de este contenedor con la fórmula exacta <b>(USD × TRM + landed por unidad) × (1 + % gastos)</b>, también los ya vendidos, para que la rentabilidad histórica quede bien. Los precios de venta solo cambian si activas la casilla de abajo.
          </p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label>TRM aplicada (COP / USD)</Label>
            <Input type="number" value={trm} onChange={(e) => { setTrm(e.target.value); setPreview(null); }} className="h-11 mt-1" data-testid="recostear-trm" />
            <div className="text-[11px] text-muted-foreground mt-1">Actual: $ {Number(contenedor.trm_aplicada || 0).toLocaleString("es-CO")}</div>
          </div>
          <div>
            <Label>% Gastos (nacionalización, fletes…)</Label>
            <Input type="number" value={pct} onChange={(e) => { setPct(e.target.value); setPreview(null); }} className="h-11 mt-1" data-testid="recostear-pct" />
            <div className="text-[11px] text-muted-foreground mt-1">Actual: {pctActual}%</div>
          </div>
          <div>
            <Label>Gastos landed totales (COP fijos)</Label>
            <Input type="number" min="0" value={landed} onChange={(e) => { setLanded(e.target.value); setPreview(null); }} className="h-11 mt-1" placeholder="0" data-testid="recostear-landed" />
            <div className="text-[11px] text-muted-foreground mt-1">Actual: {formatCOP(landedActual)} · se reparte entre todas las unidades del contenedor</div>
          </div>
          <div>
            <Label>Prorrateo del landed</Label>
            <select value={metodo} onChange={(e) => { setMetodo(e.target.value); setPreview(null); }} className="h-11 mt-1 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="recostear-metodo">
              <option value="valor">Por valor (proporcional al costo USD)</option>
              <option value="unidades">Por unidades (mismo COP por unidad)</option>
            </select>
            <div className="text-[11px] text-muted-foreground mt-1">Actual: {metodoActual === "unidades" ? "por unidades" : "por valor"}</div>
          </div>
        </div>
        <div className="mt-3 rounded-lg border border-border p-3" data-testid="recostear-precios-toggle">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={conPrecios} onChange={(e) => { setConPrecios(e.target.checked); setPreview(null); }} className="mt-0.5 accent-primary" data-testid="recostear-precios-check" />
            <div>
              <div className="text-sm font-semibold">Recalcular también precios de venta (mín / sugerido)</div>
              <div className="text-[11px] text-muted-foreground">Aplica los márgenes sobre el costo corregido a TODOS los productos de este contenedor. Queda en el historial de precios de cada producto.</div>
            </div>
          </label>
          {conPrecios && (
            <div className="grid grid-cols-2 gap-3 mt-2 pl-6">
              <div>
                <Label className="text-xs">% Margen mínimo (ganancia)</Label>
                <Input type="number" value={pctMin} onChange={(e) => { setPctMin(e.target.value); setPreview(null); }} className="h-10 mt-1" data-testid="recostear-pct-min" />
                <div className="text-[11px] text-muted-foreground mt-1">Contenedor: {contenedor.pct_margen_minimo_default ?? "—"}%</div>
              </div>
              <div>
                <Label className="text-xs">% Margen sugerido</Label>
                <Input type="number" value={pctSug} onChange={(e) => { setPctSug(e.target.value); setPreview(null); }} className="h-10 mt-1" data-testid="recostear-pct-sug" />
                <div className="text-[11px] text-muted-foreground mt-1">Contenedor: {contenedor.pct_margen_sugerido_default ?? "—"}%</div>
              </div>
            </div>
          )}
        </div>
        {!preview && (
          <Button onClick={calcular} disabled={loading || sinCambio} variant="outline" className="mt-3 h-11" data-testid="recostear-calcular-btn">
            {loading ? "Calculando..." : sinCambio ? "Cambia la TRM, el %, el landed o activa precios para calcular" : "Ver qué cambiaría"}
          </Button>
        )}
        {preview && (
          <div className="mt-3 space-y-3" data-testid="recostear-preview">
            {preview.advertencia && (
              <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2" data-testid="recostear-advertencia">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {preview.advertencia}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <Kpi label="Lotes" value={formatNumber(preview.lotes)} />
              <Kpi label="Fichas a actualizar" value={formatNumber(preview.fichas_a_actualizar)} />
              <Kpi label="Despachos a recalcular" value={formatNumber(preview.despachos_a_recalcular)} />
              <Kpi label="Factor" value={`× ${preview.factor.toFixed(4)}`} />
            </div>
            {preview.landed_nuevo !== preview.landed_viejo && (
              <div className="text-xs rounded-md border border-border p-2 bg-muted/30" data-testid="recostear-landed-resumen">
                Landed: <span className="line-through text-muted-foreground">{formatCOP(preview.landed_viejo)}</span> → <b>{formatCOP(preview.landed_nuevo)}</b> ({preview.metodo_prorrateo_nuevo === "unidades" ? "por unidades" : "por valor"})
              </div>
            )}
            {preview.actualizar_precios && (
              <div className="text-xs rounded-md border border-primary/30 bg-primary/5 p-2" data-testid="recostear-precios-resumen">
                Se actualizarán precios de <b>{preview.productos_precio_a_actualizar}</b> productos: mínimo = costo × {(1 + Number(preview.pct_margen_minimo) / 100).toFixed(2)} · sugerido = costo × {(1 + Number(preview.pct_margen_sugerido) / 100).toFixed(2)}
              </div>
            )}
            <div className="flex items-center justify-center gap-3 text-sm rounded-lg border border-border p-3 bg-muted/30">
              <div><div className="text-[10px] uppercase text-muted-foreground">Valor del lote antes</div><div className="font-bold tabular-nums">{formatCOP(preview.valor_lote_viejo)}</div></div>
              <ArrowRight className="w-4 h-4 text-primary" />
              <div><div className="text-[10px] uppercase text-muted-foreground">Valor del lote después</div><div className="font-bold tabular-nums text-primary" data-testid="recostear-valor-nuevo">{formatCOP(preview.valor_lote_nuevo)}</div></div>
            </div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Producto</th><th className="text-right p-2">Und.</th><th className="text-right p-2">Costo viejo</th><th className="text-right p-2">Landed/u</th><th className="text-right p-2">Costo nuevo</th><th className="p-2">Ficha</th>{preview.actualizar_precios && <><th className="text-right p-2">Mín nuevo</th><th className="text-right p-2">Sug nuevo</th></>}</tr></thead>
                <tbody>
                  {preview.filas.slice(0, 200).map((f) => (
                    <tr key={f.lote_id} className="border-t border-border/60">
                      <td className="p-2 truncate max-w-[260px]">{f.producto_nombre}{f.codigo && <span className="text-muted-foreground"> · {f.codigo}</span>}</td>
                      <td className="p-2 text-right tabular-nums">{formatNumber(f.cantidad_recibida)}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{formatCOP(f.costo_viejo)}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{formatCOP(f.landed_unit_cop || 0)}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{formatCOP(f.costo_nuevo)}</td>
                      <td className="p-2 text-center">{f.actualiza_ficha ? "✓" : "—"}</td>
                      {preview.actualizar_precios && <>
                        <td className="p-2 text-right tabular-nums"><span className="text-muted-foreground line-through mr-1">{formatCOP(f.precio_min_viejo)}</span>{formatCOP(f.precio_min_nuevo)}</td>
                        <td className="p-2 text-right tabular-nums"><span className="text-muted-foreground line-through mr-1">{formatCOP(f.precio_sug_viejo)}</span>{formatCOP(f.precio_sug_nuevo)}</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Label>Motivo de la corrección *</Label>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: la TRM real del pago fue 3.400, no 4.350" className="h-11 mt-1" data-testid="recostear-motivo" />
            </div>
            <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Esta acción queda en auditoría. Puedes volver a corregir después si hace falta.
            </div>
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} data-testid="recostear-cancelar-btn">Cancelar</Button>
          {preview && <Button onClick={aplicar} disabled={saving} className="bg-primary" data-testid="recostear-aplicar-btn">{saving ? "Aplicando..." : "Aplicar corrección"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
}
